import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import {
    Box,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Chip,
    IconButton,
    Tooltip,
    Card,
    CardContent,
    CardHeader,
    Grid,
    Divider,
    CircularProgress,
    Alert,
    Container,
    Stack,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Visibility as VisibilityIcon,
    ExpandMore as ExpandMoreIcon,
    AccountBalance as AccountBalanceIcon,
    Assignment as AssignmentIcon,
    Person as PersonIcon,
    AttachMoney as AttachMoneyIcon,
    Send as SendIcon,
    Receipt as ReceiptIcon,
    DirectionsCar as DirectionsCarIcon,
    Flight as FlightIcon,
    Train as TrainIcon,
    Close as CloseIcon,
    FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import approvalDetailsApiService from '../../../services/approvalDetailsApiService';
import { decryptText } from '../../../utils/encryption';
import { generateSignedUrlForFilename } from '../../../utils/s3Utils';
import { format } from 'date-fns';

const ApprovalDetails = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    
    // Get encrypted parameters from URL
    const encryptedIdFromURL = searchParams.get('id');
    const encryptedTypeFromURL = searchParams.get('type');

    const encryptedId = decryptText(encryptedIdFromURL);
    const encryptedType = decryptText(encryptedTypeFromURL);

    const [approvalData, setApprovalData] = useState({
        mainAssessment: [],
        preAssessment: [],
        qmsAssessment: [],
        reallotment: [],
        adminData: []
    });
    
    const [overallHonoranium, setOverallHonoranium] = useState({
        main: 0,
        pre: 0,
        reallotment: 0
    });
    
    const [ceoRemarks, setCeoRemarks] = useState({
        main: '',
        pre: '',
        reallot: ''
    });
    
    const [loading, setLoading] = useState(false);
    const [assessmentType, setAssessmentType] = useState('');
    const [appId, setAppId] = useState('');
    const [actionStatus, setActionStatus] = useState({
        main: { processed: false, date: '', visible: false },
        pre: { processed: false, date: '', visible: false },
        reallot: { processed: false, date: '', visible: false },
        qms: { processed: false, date: '', visible: false }
    });

    // Send to Lab for Payment dialog state
    const [sendToLabDialog, setSendToLabDialog] = useState(false);
    const [labPaymentRemarks, setLabPaymentRemarks] = useState('');
    const [currentSection, setCurrentSection] = useState('');

    // TADA Dialog state
    const [tadaDialogOpen, setTadaDialogOpen] = useState(false);
    const [selectedAssessor, setSelectedAssessor] = useState(null);
    const [tadaData, setTadaData] = useState([]);
    const [tadaLoading, setTadaLoading] = useState(false);
    const [assessorBasicDetails, setAssessorBasicDetails] = useState(null);
    const [basicDetailsLoading, setBasicDetailsLoading] = useState(false);

    useEffect(() => {
        if (encryptedId && encryptedType) {
            fetchApprovalDetails();
        }
    }, [encryptedId, encryptedType]);

    const fetchApprovalDetails = async () => {
        try {
            setLoading(true);
            const response = await approvalDetailsApiService.getApprovalDetails(encryptedId, encryptedType);
            
            if (response.success) {
                const { data } = response;
                setApprovalData(data);
                setAssessmentType(data.assessmentType);
                setAppId(data.appId);
                setOverallHonoranium(data.overallHonoranium);
                setCeoRemarks(data.ceoRemarks);
                setActionStatus(data.actionStatus);
            } else {
                toast.error(response.message || 'Error loading approval details');
            }
            
        } catch (error) {
            console.error('Error fetching approval details:', error);
            toast.error('Error loading approval details');
        } finally {
            setLoading(false);
        }
    };

    const handleAmountChange = (index, value, type, section) => {
        const updatedData = { ...approvalData };
        if (section === 'main') {
            updatedData.mainAssessment[index].finalAmount = value;
        } else if (section === 'pre') {
            updatedData.preAssessment[index].finalAmount = value;
        } else if (section === 'reallot') {
            updatedData.reallotment[index].finalAmount = value;
        } else if (section === 'qms') {
            updatedData.qmsAssessment[index].finalAmount = value;
        }
        setApprovalData(updatedData);
    };

    const handleRemarksChange = (index, value, section) => {
        const updatedData = { ...approvalData };
        if (section === 'main') {
            updatedData.mainAssessment[index].remarks = value;
        } else if (section === 'pre') {
            updatedData.preAssessment[index].remarks = value;
        } else if (section === 'reallot') {
            updatedData.reallotment[index].remarks = value;
        } else if (section === 'qms') {
            updatedData.qmsAssessment[index].remarks = value;
        }
        setApprovalData(updatedData);
    };

    const handleSubmitIndividual = async (item, section) => {
        try {
            const amountToSubmit = item.finalAmount || item.totalAmount;
            const remarks = item.remarks;
            if (!amountToSubmit) {
                toast.error('Please enter amount');
                return;
            }
            // if (!remarks || remarks.trim() === '') {
            //     toast.error('Please enter remarks');
            //     return;
            // }

            const payload = {
                appId: appId,
                assessorId: item.assessorId || item.allotedToId[0], // Handle both single and multiple allotments
                assessmentId: item.AssessorAllotmentId,
                assessmentType: section === 'main' ? 'Main' : section === 'pre' ? 'Pre' : 'QMS',
                totalAmount: amountToSubmit,
                remarks: item.remarks || '',
                isChargeable: section === 'qms' ? 0 : 1,
                verificationId: item.verificationId
            };

            const response = await approvalDetailsApiService.submitIndividualApproval(payload);
            
            if (response.success) {
                toast.success(response.message || 'Successfully submitted');
                fetchApprovalDetails(); // Refresh data
            } else {
                toast.error(response.message || 'Something went wrong. Please try again later.');
            }
        } catch (error) {
            console.error('Error submitting individual approval:', error);
            if (error.message) {
                toast.error(error.message);
            } else {
                toast.error('Error submitting approval');
            }
        }
    };

    const handleSendToCEO = async (type) => {
        try {
            const response = await approvalDetailsApiService.sendToCEO({
                appId: appId,
                assessmentType: type
            });

            if (response.success) {
                toast.success(response.message || 'Action Performed Successfully');
                fetchApprovalDetails(); // Refresh data
            } else {
                toast.error(response.message || 'Something went wrong. Please try again later.');
            }
        } catch (error) {
            console.error('Error sending to CEO:', error);
            if (error.message) {
                toast.error(error.message);
            } else {
                toast.error('Error sending to CEO');
            }
        }
    };

    const handleReallotmentProcess = async () => {
        try {
            const response = await approvalDetailsApiService.processReallotment({
                appId: appId,
                assessmentType: 'Main'
            });

            if (response.success) {
                toast.success(response.message || 'Action Performed Successfully');
                fetchApprovalDetails(); // Refresh data
            } else {
                toast.error(response.message || 'Something went wrong. Please try again later.');
            }
        } catch (error) {
            console.error('Error processing reallotment:', error);
            if (error.message) {
                toast.error(error.message);
            } else {
                toast.error('Error processing reallotment');
            }
        }
    };

    const handleViewReport = (item) => {
        // Open report in new tab
        window.open(`/preview-report?appId=${appId}&assmntId=${item.id}&assrId=${item.assessorId || item.allotedToId}`, '_blank');
    };

    const isNumberKey = (evt) => {
        const charCode = (evt.which) ? evt.which : evt.keyCode;
        return !(charCode > 31 && (charCode < 48 || charCode > 57));
    };

    // Check if all entries have final amount and remarks
    const areAllEntriesComplete = (data) => {
        if (!data || data.length === 0) return false;
        
        // Check if all entries are processed (Is_Processed = 1)
        const allProcessed = data.every(item => item.Is_Processed === 1);
        if (allProcessed) return false; // Hide button if all entries are processed
        
        // Check if all entries have final amount and remarks
        return data.every(item => {
            const finalAmount = item.finalAmount || item.totalAmount;
            const remarks = item.remarks;
            return finalAmount && finalAmount.toString().trim() !== '' && 
                   remarks && remarks.toString().trim() !== '';
        });
    };

    // Handle Send to Lab for Payment dialog
    const handleSendToLabDialog = (section) => {
        setCurrentSection(section);
        setSendToLabDialog(true);
    };

    const handleSendToLabPayment = async () => {
        try {
            if (!labPaymentRemarks.trim()) {
                toast.error('Please enter remarks');
                return;
            }

            const response = await approvalDetailsApiService.sendToLabForPayment({
                appId: appId,
                orgId: localStorage.getItem('orgId'),
                remarks: labPaymentRemarks
            });

            if (response.success) {
                toast.success('Successfully sent to Lab for Payment');
                setSendToLabDialog(false);
                setLabPaymentRemarks('');
                fetchApprovalDetails(); // Refresh data
            } else {
                toast.error(response.message || 'Something went wrong. Please try again later.');
            }
        } catch (error) {
            console.error('Error sending to lab for payment:', error);
            toast.error('Error sending to lab for payment');
        }
    };

    const handleCloseSendToLabDialog = () => {
        setSendToLabDialog(false);
        setLabPaymentRemarks('');
        setCurrentSection('');
    };

    // TADA related functions
    const fetchTadaData = async (assessorId, assessmentId) => {
        setTadaLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetAssessorTravel?assessmentid=${assessmentId}&assessorid=${assessorId}`);
            if (response.ok) {
                const data = await response.json();
                if (data.isstatus && data.travel) {
                    setTadaData(data.travel);
                } else {
                    setTadaData([]);
                }
            } else {
                setTadaData([]);
            }
        } catch (error) {
            console.error('Error fetching TADA data:', error);
            setTadaData([]);
        } finally {
            setTadaLoading(false);
        }
    };

    const fetchAssessorBasicDetails = async (assessorId, assessmentId) => {
        setBasicDetailsLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/assessor/GetAssessorBasicDetails?assessorid=${assessorId}&assessmentid=${assessmentId}`);
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data) {
                    setAssessorBasicDetails(data.data);
                } else {
                    setAssessorBasicDetails(null);
                }
            } else {
                setAssessorBasicDetails(null);
            }
        } catch (error) {
            console.error('Error fetching assessor basic details:', error);
            setAssessorBasicDetails(null);
        } finally {
            setBasicDetailsLoading(false);
        }
    };

    const handleViewTada = async (assessor) => {
        setSelectedAssessor(assessor);
        setTadaDialogOpen(true);
        
        // Fetch both TADA data and basic details in parallel
        const assessorId = assessor.allotedToId[0] || assessor.allotedToId;
        const assessmentId = assessor.AssessorAllotmentId;
        
        await Promise.all([
            fetchTadaData(assessorId, assessmentId),
            fetchAssessorBasicDetails(assessorId, assessmentId)
        ]);
    };

    const handleCloseTadaDialog = () => {
        setTadaDialogOpen(false);
        setSelectedAssessor(null);
        setTadaData([]);
        setAssessorBasicDetails(null);
    };

    const handleViewTravelBill = async (billFileName) => {
        if (!billFileName) {
            toast.error('No bill file available to view');
            return;
        }

        try {
            // Generate signed URL for the travel bill using S3
            const signedUrl = await generateSignedUrlForFilename(billFileName);
            
            if (signedUrl) {
                // Open the signed URL in a new tab
                window.open(signedUrl, '_blank');
            } else {
                toast.error('Unable to generate file access URL');
            }
        } catch (error) {
            console.error('Error viewing travel bill:', error);
            toast.error('Error accessing bill file: ' + error.message);
        }
    };
    const handleViewCancelCheck = async (cancelCheckFileName) => {
        if (!cancelCheckFileName) {
            toast.error('No cancel check file available to view');
            return;
        }

        try {
            // Generate signed URL for the cancel check using S3
            const signedUrl = await generateSignedUrlForFilename(cancelCheckFileName);

            if (signedUrl) {
                // Open the signed URL in a new tab
                window.open(signedUrl, '_blank');
            } else {
                toast.error('Unable to generate file access URL');
            }
        } catch (error) {
            console.error('Error viewing travel bill:', error);
            toast.error('Error accessing bill file: ' + error.message);
        }
    };

    const getModeIcon = (mode) => {
        switch (mode?.toLowerCase()) {
            case 'train':
            case 'railway':
                return <TrainIcon fontSize="small" />;
            case 'flight':
            case 'air':
            case 'aeroplane':
                return <FlightIcon fontSize="small" />;
            case 'road':
            case 'car':
            case 'taxi':
            case 'bus':
                return <DirectionsCarIcon fontSize="small" />;
            default:
                return <DirectionsCarIcon fontSize="small" />;
        }
    };

    const getModeColor = (mode) => {
        switch (mode?.toLowerCase()) {
            case 'train':
            case 'railway':
                return 'primary';
            case 'flight':
            case 'air':
            case 'aeroplane':
                return 'error';
            case 'road':
            case 'car':
            case 'taxi':
                return 'warning';
            case 'bus':
                return 'success';
            default:
                return 'default';
        }
    };

    const exportToExcel = async () => {
        try {
            // Dynamic import of xlsx
            const XLSX = await import('xlsx');
            
            // Create a new workbook
            const workbook = XLSX.utils.book_new();
            
            // Prepare data for different assessment types
            const allData = [];
            
            // Helper function to add assessment data with full details
            const addAssessmentData = (items, assessmentType) => {
                items.forEach((item, index) => {
                    allData.push({
                        'S.No': index + 1,
                        'Assessment Type': assessmentType,
                        'Laboratory Name': item.laboratoryName || 'N/A',
                        'Application No': item.applicationNo || appId,
                        'Discipline Name': item.disciplineName || 'N/A',
                        'Group': item.groups || 'N/A',
                        'Assessor Name': item.assessorName || 'N/A',
                        'Assessor ID': item.loginName || 'N/A',
                        'Capacity': item.capacity || 'N/A',
                        'Contact': item.contact || 'N/A',
                        'Current Employer': item.currentEmployer || 'N/A',
                        'Assessment From Date': item.dateFrom || 'N/A',
                        'Assessment To Date': item.dateTo || 'N/A',
                        'Assessment Days': item.days || 'N/A',
                        'Honorarium Amount': item.honAmount || 0,
                        'Travel & Other Amount': item.amount || 0,
                        'Total Amount': item.totalAmount || (parseInt(item.honAmount || 0) + parseInt(item.amount || 0)),
                        'Final Approved Amount': item.finalAmount || item.totalAmount || 0,
                        'Remarks': item.remarks || 'N/A',
                        'Status': item.Is_Processed === 1 ? 'Processed' : 'Pending',
                        'Processed Date': item.Processed_Date || 'N/A'
                    });
                });
            };
            
            // Add Main Assessment data
            if (approvalData.mainAssessment && approvalData.mainAssessment.length > 0) {
                addAssessmentData(approvalData.mainAssessment, 'Main Assessment');
            }
            
            // Add Pre Assessment data
            if (approvalData.preAssessment && approvalData.preAssessment.length > 0) {
                addAssessmentData(approvalData.preAssessment, 'Pre Assessment');
            }
            
            // Add QMS Assessment data
            if (approvalData.qmsAssessment && approvalData.qmsAssessment.length > 0) {
                addAssessmentData(approvalData.qmsAssessment, 'QMS Assessment');
            }
            
            // Add Reallotment data
            if (approvalData.reallotment && approvalData.reallotment.length > 0) {
                addAssessmentData(approvalData.reallotment, 'Reallotment Assessment');
            }
            
            // Create worksheet from data
            if (allData.length > 0) {
                const worksheet = XLSX.utils.json_to_sheet(allData);
                
                // Auto-size columns
                const colWidths = [];
                const headers = Object.keys(allData[0]);
                headers.forEach((header, index) => {
                    let maxWidth = header.length;
                    allData.forEach(row => {
                        const cellValue = String(row[header] || '');
                        maxWidth = Math.max(maxWidth, cellValue.length);
                    });
                    colWidths[index] = { wch: Math.min(maxWidth + 2, 50) }; // Max width 50 characters
                });
                worksheet['!cols'] = colWidths;
                
                // Add the worksheet to workbook
                XLSX.utils.book_append_sheet(workbook, worksheet, 'Assessment Details');
                
                // Add Admin Data if available
                if (approvalData.adminData && approvalData.adminData.length > 0) {
                    const adminData = approvalData.adminData.map((item, index) => ({
                        'S.No': index + 1,
                        'Laboratory Name': item.laboratoryName || 'N/A',
                        'Assessment Type': item.assessmentType || 'N/A',
                        'Assessor Name': item.assessorName || 'N/A',
                        'Capacity': item.capacity || 'N/A',
                        'From Date': item.dateFrom || 'N/A',
                        'To Date': item.dateTo || 'N/A',
                        'Admin Proposed Amount': item.amount || 0,
                        'Dealing Office Remarks': item.remark || 'N/A',
                        'Admin Remarks': item.adminRemarks || 'N/A'
                    }));
                    
                    const adminWorksheet = XLSX.utils.json_to_sheet(adminData);
                    XLSX.utils.book_append_sheet(workbook, adminWorksheet, 'Admin Proposed Details');
                }
                
                // Generate filename with current date
                const currentDate = new Date().toISOString().split('T')[0];
                const filename = `Assessment_Approval_Details_${appId}_${currentDate}.xlsx`;
                
                // Save the file
                XLSX.writeFile(workbook, filename);
                
                toast.success(`Excel file "${filename}" downloaded successfully!`);
            } else {
                toast.warning('No data available to export');
            }
            
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            toast.error('Error exporting data to Excel. Please make sure xlsx library is installed.');
        }
    };

    const renderAssessmentTable = (data, section, title, honoranium) => {
        if (!data || data.length === 0) return null;

        return (
            <Accordion defaultExpanded sx={{ mb: 3 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AssignmentIcon color="primary" />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {title}
                        </Typography>
                        {/* <Chip 
                            label={`₹${honoranium}`} 
                            color="success" 
                            variant="outlined"
                            size="small"
                            icon={<AttachMoneyIcon />}
                        /> */}
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Paper elevation={2}>
                        <Box sx={{ p: 2, bgcolor: 'primary.50' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                Overall Honorarium Amount: ₹{honoranium}
                            </Typography>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                                        <TableCell sx={{ fontWeight: 600, minWidth: 50 }}>S. No</TableCell>
                                        <TableCell sx={{ fontWeight: 600, minWidth: 250 }}>Laboratory & Assessment Info</TableCell>
                                        <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Assessor Details</TableCell>
                                        <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Assessment Period</TableCell>
                                        <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>Amounts</TableCell>
                                        <TableCell sx={{ fontWeight: 600, minWidth: 220 }}>Final Amount & Remarks</TableCell>
                                        {/* <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Documents</TableCell> */}
                                        <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.map((item, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell>{index + 1}</TableCell>
                                            {/* Laboratory & Assessment Info */}
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600} noWrap>
                                                        {item.laboratoryName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.primary">
                                                        Application No: {item.applicationNo}
                                                    </Typography>
                                                    <Divider sx={{ my: 0.5 }} />
                                                    <Typography variant="body2" sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                                        {item.disciplineName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.primary">
                                                        Group: {item.groups || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            {/* Assessor Details */}
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {item.assessorName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.primary">
                                                        ID: {item.loginName}
                                                    </Typography>
                                                    <br />
                                                    <Chip 
                                                        label={item.capacity} 
                                                        size="small" 
                                                        color="secondary"
                                                        variant="outlined"
                                                        sx={{ mt: 0.5 }}
                                                    />
                                                    <Typography variant="caption" display="block" color="text.primary">
                                                       Contact: {item.contact}
                                                    </Typography>
                                                    <Typography variant="caption" display="block" color="text.primary" sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                                        Organisation: {item.currentEmployer}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            {/* Assessment Period */}
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2">
                                                        <strong>From:</strong> {item.dateFrom}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <strong>To:</strong> {item.dateTo}
                                                    </Typography>
                                                    {/* <Box sx={{ mt: 1 }}>
                                                        {item.visFile === 0 ? (
                                                            <Chip label="Report N/A" size="small" color="error" variant="outlined" />
                                                        ) : (
                                                            <IconButton 
                                                                size="small" 
                                                                color="primary"
                                                                onClick={() => handleViewReport(item)}
                                                            >
                                                                <VisibilityIcon />
                                                            </IconButton>
                                                        )}
                                                    </Box> */}
                                                </Box>
                                            </TableCell>
                                            {/* Amounts */}
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" color="success.main" fontWeight={600}>
                                                        Hon: ₹{item.honAmount}
                                                    </Typography>
                                                    <Typography variant="body2" color="primary.main" fontWeight={600}>
                                                        Amount: ₹{item.amount}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            {/* Final Amount & Remarks */}
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    <Box>
                                                        <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5 }}>
                                                            Final Amount:
                                                        </Typography>
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            variant="outlined"
                                                            sx={{ width: '100%' }}
                                                            placeholder="Amount"
                                                            value={item.finalAmount || item.totalAmount || ''}
                                                            onChange={(e) => handleAmountChange(index, e.target.value, 'amount', section)}
                                                            disabled={!item.visSubmitBtn}
                                                        />
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5 }}>
                                                            Remarks:
                                                        </Typography>
                                                        <TextField
                                                            size="small"
                                                            multiline
                                                            rows={2}
                                                            variant="outlined"
                                                            sx={{ width: '100%' }}
                                                            placeholder="Enter remarks"
                                                            value={item.remarks || ''}
                                                            onChange={(e) => handleRemarksChange(index, e.target.value, section)}
                                                            disabled={!item.visSubmitBtn}
                                                        />
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            {/* Documents */}
                                            {/* <TableCell>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    <Box>
                                                        <Typography variant="caption" fontWeight={600}>PAN:</Typography>
                                                        <Typography variant="caption" display="block">
                                                            {item.panUploadFile || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" fontWeight={600}>Cheque:</Typography>
                                                        {item.cancelChequeUploadfile ? (
                                                            <Button 
                                                                size="small" 
                                                                variant="text"
                                                                href={item.cancelChequeUploadfile} 
                                                                target="_blank"
                                                                sx={{ p: 0, minWidth: 'auto', fontSize: '0.7rem' }}
                                                            >
                                                                View
                                                            </Button>
                                                        ) : (
                                                            <Typography variant="caption" color="text.primary">N/A</Typography>
                                                        )}
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" fontWeight={600}>New PAN:</Typography>
                                                        {item.newPanUploadFile ? (
                                                            <Button 
                                                                size="small" 
                                                                variant="text"
                                                                href={item.newPanUploadFile} 
                                                                target="_blank"
                                                                sx={{ p: 0, minWidth: 'auto', fontSize: '0.7rem' }}
                                                            >
                                                                View
                                                            </Button>
                                                        ) : (
                                                            <Typography variant="caption" color="text.primary">N/A</Typography>
                                                        )}
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" fontWeight={600}>New Cheque:</Typography>
                                                        {item.newCancelChequeUploadfile ? (
                                                            <Button 
                                                                size="small" 
                                                                variant="text"
                                                                href={item.newCancelChequeUploadfile} 
                                                                target="_blank"
                                                                sx={{ p: 0, minWidth: 'auto', fontSize: '0.7rem' }}
                                                            >
                                                                View
                                                            </Button>
                                                        ) : (
                                                            <Typography variant="caption" color="text.primary">N/A</Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </TableCell> */}
                                            {/* Action */}
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                                                    {item.visSubmitBtn && (
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            color={item.commandName === 'Update' ? 'warning' : 'primary'}
                                                            onClick={() => handleSubmitIndividual(item, section)}
                                                            sx={{ minWidth: 70, fontSize: '0.75rem' }}
                                                        >
                                                            {item.commandName === 'Update' ? 'Update' : 'Submit'}
                                                        </Button>
                                                    )}
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="info"
                                                        onClick={() => handleViewTada(item)}
                                                        startIcon={<ReceiptIcon />}
                                                        sx={{ minWidth: 70, fontSize: '0.75rem' }}
                                                    >
                                                        View TADA
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </AccordionDetails>
            </Accordion>
        );
    };


    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
                    <CircularProgress size={60} />
                    <Typography variant="h6" sx={{ mt: 2 }}>
                        Loading approval details...
                    </Typography>
                </Paper>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
            {/* Header Section */}
            <ToastContainer />
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <AccountBalanceIcon color="primary" sx={{ fontSize: 32 }} />
                        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Accounts Approval
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button 
                            variant="contained"
                            color="success"
                            startIcon={<FileDownloadIcon />}
                            onClick={exportToExcel}
                            sx={{ minWidth: 160 }}
                        >
                            Export to Excel
                        </Button>
                        <Button 
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate(-1)}
                            sx={{ minWidth: 120 }}
                        >
                            Go Back
                        </Button>
                    </Box>
                </Box>
                <Divider sx={{ mt: 2 }} />
            </Paper>

            {/* Content Sections */}
            <Box sx={{ minHeight: 400 }}>
                {/* Main Assessment Section */}
                {assessmentType === 'Main' && approvalData.mainAssessment.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        {renderAssessmentTable(
                            approvalData.mainAssessment, 
                            'main', 
                            'Alloted Assessors For Assessment', 
                            overallHonoranium.main
                        )}
                        
                        {/* CEO Remarks */}
                        {ceoRemarks.main && (
                            <Card sx={{ mb: 2, bgcolor: 'warning.50', borderLeft: 4, borderColor: 'warning.main' }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                        CEO Remarks:
                                    </Typography>
                                    <Typography variant="body1" color="text.primary">
                                        {ceoRemarks.main}
                                    </Typography>
                                </CardContent>
                            </Card>
                        )}
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                        {/* Action Status */}
                        {actionStatus.main.processed ? (
                            <Alert severity="success" sx={{ justifyContent: 'center' }}>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    Action Performed Successfully on {actionStatus.main.date}
                                </Typography>
                            </Alert>    
                        ) : approvalData.mainAssessment.every(item => item.Is_Processed === 1) ? (
                            <Alert severity="success" sx={{ justifyContent: 'center' }}>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    All entries have been processed on {approvalData.mainAssessment[0].Processed_Date ? format(new Date(approvalData.mainAssessment[0].Processed_Date), 'dd MMM yyyy') : ''}
                                </Typography>
                            </Alert>
                        ) : areAllEntriesComplete(approvalData.mainAssessment) ? (
                            <Button 
                                variant="contained"
                                size="large"
                                onClick={() => handleSendToLabDialog('main')}
                                startIcon={<SendIcon />}
                                sx={{ px: 4, py: 1.5 }}
                                color="success"
                            >
                                Send to Lab for Payment
                            </Button>
                        ) : null}
                        </Box>
                    </Box>
                )}

                {/* Reallotment Section */}
                {assessmentType === 'Main' && approvalData.reallotment.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        {renderAssessmentTable(
                            approvalData.reallotment, 
                            'reallot', 
                            'Realloted Assessors For Assessment', 
                            overallHonoranium.reallotment
                        )}
                        
                        {/* CEO Remarks */}
                        {ceoRemarks.reallot && (
                            <Card sx={{ mb: 2, bgcolor: 'warning.50', borderLeft: 4, borderColor: 'warning.main' }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                        CEO Remarks:
                                    </Typography>
                                    <Typography variant="body1" color="text.primary">
                                        {ceoRemarks.reallot}
                                    </Typography>
                                </CardContent>
                            </Card>
                        )}
                        
                        {/* Action Status */}
                        <Card sx={{ textAlign: 'center', p: 2 }}>
                            {actionStatus.reallot.processed ? (
                                <Alert severity="success" sx={{ justifyContent: 'center' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        Action Performed Successfully on {actionStatus.reallot.date}
                                    </Typography>
                                </Alert>
                            ) : approvalData.reallotment.every(item => item.Is_Processed === 1) ? (
                                <Alert severity="success" sx={{ justifyContent: 'center' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        All entries have been processed
                                    </Typography>
                                </Alert>
                            ) : areAllEntriesComplete(approvalData.reallotment) ? (
                                <Button 
                                    variant="contained"
                                    size="large"
                                    onClick={() => handleSendToLabDialog('reallot')}
                                    startIcon={<SendIcon />}
                                    sx={{ px: 4, py: 1.5 }}
                                    color="success"
                                >
                                    Send to Lab for Payment
                                </Button>
                            ) : (
                                <Alert severity="info" sx={{ justifyContent: 'center' }}>
                                    <Typography variant="body2">
                                        Please fill final amount and remarks for all entries to proceed
                                    </Typography>
                                </Alert>
                            )}
                        </Card>
                    </Box>
                )}

                {/* Pre Assessment Section */}
                {assessmentType === 'Pre' && approvalData.preAssessment.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        {renderAssessmentTable(
                            approvalData.preAssessment, 
                            'pre', 
                            'Alloted Assessors For Pre-Assessment', 
                            overallHonoranium.pre
                        )}
                        
                        {/* CEO Remarks */}
                        {ceoRemarks.pre && (
                            <Card sx={{ mb: 2, bgcolor: 'warning.50', borderLeft: 4, borderColor: 'warning.main' }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                        CEO Remarks:
                                    </Typography>
                                    <Typography variant="body1" color="text.primary">
                                        {ceoRemarks.pre}
                                    </Typography>
                                </CardContent>
                            </Card>
                        )}
                        
                        {/* Action Status */}
                        <Card sx={{ textAlign: 'center', p: 2 }}>
                            {actionStatus.pre.processed ? (
                                <Alert severity="success" sx={{ justifyContent: 'center' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        Action Performed Successfully on {actionStatus.pre.date}
                                    </Typography>
                                </Alert>
                            ) : approvalData.preAssessment.every(item => item.Is_Processed === 1) ? (
                                <Alert severity="success" sx={{ justifyContent: 'center' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        All entries have been processed
                                    </Typography>
                                </Alert>
                            ) : areAllEntriesComplete(approvalData.preAssessment) ? (
                                <Button 
                                    variant="contained"
                                    size="large"
                                    onClick={() => handleSendToLabDialog('pre')}
                                    startIcon={<SendIcon />}
                                    sx={{ px: 4, py: 1.5 }}
                                    color="success"
                                >
                                    Send to Lab for Payment
                                </Button>
                            ) : (
                                <Alert severity="info" sx={{ justifyContent: 'center' }}>
                                    <Typography variant="body2">
                                        Please fill final amount and remarks for all entries to proceed
                                    </Typography>
                                </Alert>
                            )}
                        </Card>
                    </Box>
                )}


                {/* Admin Data Section */}
                {approvalData.adminData.length > 0 && (
                    <Accordion sx={{ mb: 3 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonIcon color="info" />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Admin Proposed Details
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Paper elevation={2}>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                                                <TableCell sx={{ fontWeight: 600 }}>S.No</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Laboratory Name</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Assessment Type</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Assessor</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Capacity</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>From Date</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>To Date</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Dealing Office Travel Plan</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Admin Proposed Amount</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Admin Travel Plan Detail</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {approvalData.adminData.map((item, index) => (
                                                <TableRow key={index} hover>
                                                    <TableCell>{index + 1}</TableCell>
                                                    <TableCell>{item.laboratoryName}</TableCell>
                                                    <TableCell>
                                                        <Chip label={item.assessmentType} size="small" color="primary" />
                                                    </TableCell>
                                                    <TableCell>{item.assessorName}</TableCell>
                                                    <TableCell>
                                                        <Chip label={item.capacity} size="small" color="secondary" variant="outlined" />
                                                    </TableCell>
                                                    <TableCell>{item.dateFrom}</TableCell>
                                                    <TableCell>{item.dateTo}</TableCell>
                                                    <TableCell>
                                                        <Stack spacing={1}>
                                                            <Button 
                                                                size="small" 
                                                                variant="outlined"
                                                                href={item.files} 
                                                                target="_blank"
                                                                startIcon={<VisibilityIcon />}
                                                            >
                                                                View File
                                                            </Button>
                                                            <Typography variant="caption" color="text.primary">
                                                                <strong>Remarks:</strong> {item.remark}
                                                            </Typography>
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="success.main" fontWeight={600}>
                                                            ₹{item.amount}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Stack spacing={1}>
                                                            <Button 
                                                                size="small" 
                                                                variant="outlined"
                                                                href={item.adminUpload} 
                                                                target="_blank"
                                                                startIcon={<VisibilityIcon />}
                                                            >
                                                                View File
                                                            </Button>
                                                            <Typography variant="caption" color="text.primary">
                                                                <strong>Remarks:</strong> {item.adminRemarks}
                                                            </Typography>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </AccordionDetails>
                    </Accordion>
                )}

                {/* No Data Message */}
                {!loading && 
                 assessmentType && 
                 approvalData.mainAssessment.length === 0 && 
                 approvalData.preAssessment.length === 0 && 
                 approvalData.qmsAssessment.length === 0 && (
                    <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
                        <Alert severity="info">
                            <Typography variant="h6">No approval data available</Typography>
                            <Typography variant="body2" color="text.primary">
                                No assessment data found for this application and assessment type.
                            </Typography>
                        </Alert>
                    </Paper>
                )}
            </Box>

            {/* Send to Lab for Payment Dialog */}
            <Dialog
                open={sendToLabDialog}
                onClose={handleCloseSendToLabDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SendIcon color="success" />
                        Send to Lab for Payment
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Please enter remarks before sending to lab for payment.
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Remarks"
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        value={labPaymentRemarks}
                        onChange={(e) => setLabPaymentRemarks(e.target.value)}
                        placeholder="Enter remarks for lab payment..."
                        required
                    />
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button 
                        onClick={handleCloseSendToLabDialog}
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSendToLabPayment}
                        variant="contained"
                        color="success"
                        startIcon={<SendIcon />}
                        disabled={!labPaymentRemarks.trim()}
                    >
                        Send for Payment
                    </Button>
                </DialogActions>
            </Dialog>

            {/* TADA Details Dialog */}
            <Dialog
                open={tadaDialogOpen}
                onClose={handleCloseTadaDialog}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { maxHeight: '90vh', overflow: 'hidden' }
                }}
            >
                <DialogTitle sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    pb: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ReceiptIcon color="primary" />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            TADA Details - {selectedAssessor?.assessorName || 'N/A'}
                        </Typography>
                    </Box>
                    <IconButton onClick={handleCloseTadaDialog} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                
                <DialogContent sx={{ p: 0 }}>
                    {(tadaLoading || basicDetailsLoading) ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box sx={{ p: 3 }}>
                            {/* Assessor Basic Details Section */}
                            {assessorBasicDetails && (
                                <Card sx={{ mb: 3 }}>
                                    <CardHeader
                                        avatar={<PersonIcon color="primary" />}
                                        title="Assessor Basic Details"
                                        sx={{ pb: 1 }}
                                    />
                                    <CardContent sx={{ pt: 0 }}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                        Name
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {assessorBasicDetails.Name || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                        Email
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {assessorBasicDetails.Officeemail || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                        Mobile
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {assessorBasicDetails.OfficeMobile || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                        PAN Number
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {assessorBasicDetails.PANCard || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                        Address
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {assessorBasicDetails.address || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                        Bank Details
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {assessorBasicDetails.Bank_Details || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                        Account Number
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {assessorBasicDetails.Account_No || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                        IFSC Code
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {assessorBasicDetails.Ifcs_Code || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                        Bank Address
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {assessorBasicDetails.Bank_Address || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            {assessorBasicDetails.Car_RegNo && (
                                                <Grid item xs={12} sm={6}>
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                            Car Registration No
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {assessorBasicDetails.Car_RegNo}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            )}
                                            <Grid item xs={12}>
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                      Assessor  Organization Name
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {assessorBasicDetails.currentEmployer || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                        Purpose & Assessment Period
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {assessorBasicDetails.Purpose || 'N/A'} 
                                                        {assessorBasicDetails.DateFrom && assessorBasicDetails.DateTo && 
                                                            ` & ${assessorBasicDetails.DateFrom} To ${assessorBasicDetails.DateTo}`
                                                        }
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item md={6} sm={12}>
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                        Cancel Cheque
                                                    </Typography>
                                                    <Typography variant="body2">
                                                       {assessorBasicDetails.CancelChequeUploadfile ? (
                                                           <Button
                                                                        size="small"
                                                                        variant="contained"
                                                                        color="primary"
                                                                        onClick={() => handleViewCancelCheck(assessorBasicDetails.CancelChequeUploadfile)}
                                                                        startIcon={<VisibilityIcon />}
                                                                        sx={{ fontSize: '0.7rem', minWidth: 'auto', p: 0.5 }}
                                                                    >
                                                                        View Cheque
                                                                    </Button>
                                                       ) : (
                                                           'N/A'
                                                       )}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item md={6} sm={12}>
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                        View PAN
                                                    </Typography>
                                                    <Typography variant="body2">
                                                       {assessorBasicDetails.UploadPanFile ? (
                                                           <Button
                                                                        size="small"
                                                                        variant="contained"
                                                                        color="primary"
                                                                        onClick={() => handleViewCancelCheck(assessorBasicDetails.UploadPanFile)}
                                                                        startIcon={<VisibilityIcon />}
                                                                        sx={{ fontSize: '0.7rem', minWidth: 'auto', p: 0.5 }}
                                                                    >
                                                                        View PAN
                                                                    </Button>
                                                       ) : (
                                                           'N/A'
                                                       )}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Travel Claims Section */}
                            {tadaData.length > 0 ? (
                                <Card>
                                    <CardHeader
                                        avatar={<DirectionsCarIcon color="success" />}
                                        title="Travel Claims"
                                        sx={{ pb: 1 }}
                                    />
                                    <CardContent sx={{ pt: 0 }}>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                                                        <TableCell sx={{ fontWeight: 600 }}>Mode</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>Route</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>Date & Time</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>Bills</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {tadaData.map((claim, index) => (
                                                        <TableRow key={claim.id || index} hover>
                                                        <TableCell>
                                                            <Chip
                                                                icon={getModeIcon(claim.modeOfTravel)}
                                                                label={claim.modeOfTravel}
                                                                size="small"
                                                                color={getModeColor(claim.modeOfTravel)}
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box>
                                                                <Typography variant="body2" fontWeight={500}>
                                                                    {claim.dep_From} → {claim.arrival_To}
                                                                </Typography>
                                                                {/* {claim.distance && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        Distance: {claim.distance} km
                                                                    </Typography>
                                                                )} */}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box>
                                                                <Typography variant="body2" fontWeight={500}>
                                                                    Departure: {claim.dep_Date}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {claim.dep_Time}
                                                                </Typography>
                                                                <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                                                                    Arrival: {claim.arrival_Date}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {claim.arrival_Time}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" fontWeight={600} color="success.main">
                                                                ₹{claim.amount}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={claim.is_Origional_Bill === "1" ? 'Available' : 'Not Available'}
                                                                size="small"
                                                                color={claim.is_Origional_Bill === "1" ? 'success' : 'error'}
                                                                variant="filled"
                                                            />
                                                            {claim.bill && (
                                                                <Box sx={{ mt: 0.5 }}>
                                                                    <Button
                                                                        size="small"
                                                                        variant="text"
                                                                        color="primary"
                                                                        onClick={() => handleViewTravelBill(claim.bill)}
                                                                        startIcon={<VisibilityIcon />}
                                                                        sx={{ fontSize: '0.7rem', minWidth: 'auto', p: 0.5 }}
                                                                    >
                                                                        View Bill
                                                                    </Button>
                                                                </Box>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label="Submitted"
                                                                size="small"
                                                                color="success"
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    
                                    {/* Total Amount */}
                                    <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1, textAlign: 'right' }}>
                                        <Typography variant="h6" fontWeight={600} color="#fff">
                                            Total Claim Amount: ₹{tadaData.reduce((sum, claim) => sum + parseInt(claim.amount || 0), 0)}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        ) : (
                            <Card>
                                <CardContent>
                                    <Box sx={{ textAlign: 'center', p: 4 }}>
                                        <ReceiptIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                                        <Typography variant="h6" color="text.secondary" gutterBottom>
                                            No TADA Details Available
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            No travel claims have been submitted for this assessor.
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        )}
                        </Box>
                    )}
                </DialogContent>
                
                <DialogActions sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button 
                        onClick={handleCloseTadaDialog}
                        variant="contained"
                        color="primary"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ApprovalDetails;